<script lang="ts" setup>
import { format } from 'date-fns'
import { Contract } from '@injectivelabs/sdk-ts'
import { sharedEllipsisFormatText } from '@shared/utils/formatter'
import {
  DEFAULT_TRUNCATE_LENGTH,
  DEFAULT_DATE_TIME_FORMAT
} from '@/app/utils/constant'
import { CodeSubPage, AccountSubPage, TransactionSubPage } from '@/types'

const { sm } = useSharedBreakpoints()

const props = withDefaults(
  defineProps<{
    symbol?: string
    contract: Contract
  }>(),
  {
    symbol: undefined
  }
)

const lastExecutedTimestamp = !props.contract.lastExecutedAt
  ? ''
  : format(props.contract.lastExecutedAt, DEFAULT_DATE_TIME_FORMAT)

const instantiatedAtTimestamp = !props.contract.instantiatedAt
  ? ''
  : format(props.contract.instantiatedAt, DEFAULT_DATE_TIME_FORMAT)

const contractDecimals = computed(() => {
  if (
    !props.contract ||
    !props.contract.initMessage ||
    !props.contract.initMessage.decimals
  ) {
    return
  }

  return props.contract.initMessage.decimals
})
</script>

<template>
  <div>
    <div class="grid gap-4 pt-6 text-sm">
      <div class="font-medium text-white text-sm">
        {{ $t('common.overview') }}
      </div>

      <AppCardContentRow :title="$t('smartContracts.contract.address')">
        <AppCopyButton
          :value="contract.address"
          :toast="$t('toast.contractCopied')"
        >
          <span class="break-all">
            {{
              sm
                ? contract.address
                : sharedEllipsisFormatText(
                    contract.address,
                    DEFAULT_TRUNCATE_LENGTH
                  )
            }}
          </span>
        </AppCopyButton>
      </AppCardContentRow>

      <AppCardContentRow
        v-bind="{ title: $t('smartContracts.contract.codeId') }"
      >
        <NuxtLink
          class="text-purple-400 hover:opacity-70"
          :to="{
            name: CodeSubPage.IndexPage,
            params: { code: contract.codeId }
          }"
        >
          {{ contract.codeId }}
        </NuxtLink>
      </AppCardContentRow>

      <AppCardContentRow
        v-bind="{ title: $t('smartContracts.contract.contract') }"
      >
        <AppPill>{{ $t('account.cw20') }}</AppPill>
      </AppCardContentRow>

      <AppCardContentRow
        v-bind="{ title: $t('smartContracts.contract.creator') }"
      >
        <NuxtLink
          class="break-all text-uiPrimary-400 hover:opacity-70"
          :to="{
            name: AccountSubPage.IndexPage,
            params: { account: contract.creator }
          }"
        >
          {{
            sm
              ? contract.creator
              : sharedEllipsisFormatText(
                  contract.creator,
                  DEFAULT_TRUNCATE_LENGTH
                )
          }}
        </NuxtLink>
      </AppCardContentRow>

      <AppCardContentRow
        v-bind="{ title: $t('smartContracts.contract.admin') }"
      >
        <span v-if="!contract.admin"> &mdash; </span>
        <NuxtLink
          v-else
          class="break-all text-uiPrimary-400 hover:opacity-70"
          :to="{
            name: AccountSubPage.IndexPage,
            params: { account: contract.admin }
          }"
        >
          {{
            sm
              ? contract.admin
              : sharedEllipsisFormatText(
                  contract.admin,
                  DEFAULT_TRUNCATE_LENGTH
                )
          }}
        </NuxtLink>
      </AppCardContentRow>

      <AppCardContentRow
        v-bind="{ title: $t('smartContracts.contract.txHash') }"
      >
        <NuxtLink
          class="break-all text-uiPrimary-400 hover:opacity-70"
          :to="{
            name: TransactionSubPage.IndexPage,
            params: { transaction: contract.txHash }
          }"
        >
          {{
            sm
              ? contract.txHash
              : sharedEllipsisFormatText(
                  contract.txHash,
                  DEFAULT_TRUNCATE_LENGTH
                )
          }}
        </NuxtLink>
      </AppCardContentRow>

      <AppCardContentRow
        v-if="symbol"
        v-bind="{ title: $t('smartContracts.contract.denom') }"
      >
        {{ symbol }}
      </AppCardContentRow>

      <AppCardContentRow
        v-if="contractDecimals"
        v-bind="{ title: $t('smartContracts.contract.decimal') }"
      >
        {{ contractDecimals }}
      </AppCardContentRow>

      <AppCardContentRow
        v-bind="{ title: $t('smartContracts.contract.executed') }"
      >
        {{ contract.executes }}
      </AppCardContentRow>

      <AppCardContentRow
        v-bind="{ title: $t('smartContracts.contract.lastExecutedAt') }"
      >
        {{ lastExecutedTimestamp }}
      </AppCardContentRow>

      <AppCardContentRow
        v-bind="{ title: $t('smartContracts.contract.creationDate') }"
      >
        {{ instantiatedAtTimestamp }}
      </AppCardContentRow>
    </div>
  </div>
</template>
